import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../../../utils/reduxHelper";
import { getSearchId, triggerGa } from "../../../utils/gaUtils";
import { GA_ACTION, GA_CATEGORIES, GA_PAGE } from "../../../utils/constants";
import { determinePLPEmpty } from "../../utils/gaUtils";

export const sourcingCollectionsReduxAPI = createApi({
  reducerPath: "sourcingCollectionReduxAPI",
  baseQuery: baseQueryWithArgs,

  tagTypes: [
    "collectionDetails",
    "plpCollections",
    "collectionProducts",
    "filterSizeOptions",
    "filterColorOptions",
    "filterBrandOptions",
    "filterCategoriesOptions",
    "filterDiscountOptions",
    "filterTagsOptions",
    "filterGenderOptions",
    "DropOffSurveyStatus",
    "IntentCaptureStatus",
    "attributeFilters",
  ],

  endpoints: (builder) => ({
    //GET Queries
    getCollectionDetails: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getCollectionDetails?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["collectionDetails"],
      keepUnusedDataFor: 60,
    }),
    getPLPCollections: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(`s/getPLPCollections?brandId=${brandId}`),
      providesTags: ["plpCollections"],
      keepUnusedDataFor: 300,
    }),
    getCollectionProducts: builder.query({
      query: ({
        collectionId,
        brandId,
        categoryId = 0,
        subcategoryId,
        sortBy,
        search,
        filters,
        page,
        attributeFilters = {},
        plpCollectionId = 0,
      }) => {
        const searchText = encodeURIComponent(search);
        const filter = encodeURIComponent(JSON.stringify(filters));
        const attrFilters = encodeURIComponent(
          JSON.stringify(attributeFilters)
        );
        const plpPart =
          plpCollectionId && Number(plpCollectionId) > 0
            ? `&plpCollectionId=${plpCollectionId}`
            : "";
        return getQueryStringWithCreator(
          `s/getCollectionProducts?collectionId=${collectionId}&brandId=${brandId}&categoryId=${categoryId}&subcategoryId=${subcategoryId}&sortBy=${sortBy}&search=${searchText}&filters=${filter}&attributeFilters=${attrFilters}&page=${page}&limit=20${plpPart}`
        );
      },
      transformResponse: (response, meta, arg) => {
        const search = (arg?.search || "").trim();
        if (
          arg?.variant === "brand-sourcing" &&
          (arg?.page || 1) === 1 &&
          search &&
          response?.success &&
          arg?.searchId &&
          arg.searchId === getSearchId("brand")
        ) {
          triggerGa({
            category: GA_CATEGORIES.USER_INTERACTION,
            action: GA_ACTION.SEARCH_RESULTS_LOAD,
            label: search,
            label2: String(response?.num_pages ?? ""),
            page: GA_PAGE.BRAND_SOURCING,
            screenName: determinePLPEmpty(
              new URLSearchParams(window.location.search)
            ),
            eventValue:
              response?.total_count ?? response?.products?.length ?? 0,
            collection_id: Number(arg?.collectionId) || 0,
            searchSurface: "brand",
          });
        }
        response.pages = [response.products];
        return response;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          brandId,
          collectionId,
          filters,
          search,
          sortBy,
          subcategoryId,
          categoryId = 0,
          attributeFilters = {},
          plpCollectionId = 0,
          searchId = "",
        } = queryArgs;
        return `endpointName=${endpointName}&brandId=${brandId}&collectionId=${collectionId}&filters=${JSON.stringify(
          filters
        )}&search=${search}&searchId=${searchId}&sortBy=${sortBy}&subcategoryId=${subcategoryId}&categoryId=${categoryId}&attributeFilters=${JSON.stringify(
          attributeFilters
        )}&plpCollectionId=${plpCollectionId || 0}`;
      },
      merge: (currentCache, newItems, otherArgs) => {
        const {
          arg: { page },
        } = otherArgs;
        if (currentCache.pages === undefined) currentCache.pages = [];
        const products = Array.isArray(newItems?.products)
          ? newItems.products
          : [];
        currentCache.pages[page - 1] = [...products];
      },
      // Keep loaded pages alive across a PDP visit so back-navigation restores
      // the full list without refetching (default 60s is shorter than a
      // typical PDP browse).
      keepUnusedDataFor: 600,
      forceRefetch({ currentArg, previousArg }) {
        // Fetch only when the requested page actually changes. Everything
        // else that matters (sort/filter/search/collection) is part of the
        // cache key, so changing it creates a fresh entry that fetches on its
        // own. Comparing object references here (`currentArg !== previousArg`)
        // is always true — args are rebuilt every render — which caused a
        // pointless refetch on every back-navigation from the PDP.
        return currentArg?.page !== previousArg?.page;
      },
    }),
    getFilterSizeOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
      }) => {
        let url = `s/getFilterSizeOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterSizeOptions"],
    }),
    getFilterColorOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
      }) => {
        let url = `s/getFilterColorOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterColorOptions"],
    }),
    getFilterBrandOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
      }) => {
        let url = `s/getFilterBrandOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterBrandOptions"],
    }),
    getFilterCategoriesOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
      }) => {
        let url = `s/getFilterCategoriesOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterCategoriesOptions"],
    }),
    getFilterDiscountOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId, filters }) => {
        let url = `s/getFilterDiscountOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterDiscountOptions"],
    }),
    getFilterTagsOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
      }) => {
        let url = `s/getFilterTagOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`;
        if (filters)
          url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterTagsOptions"],
    }),
    getFilterGenderOptions: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        plpCollectionId = 0,
      }) => {
        let url = `s/getFilterHeaderOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["filterGenderOptions"],
    }),

    // ─── Drop-off survey ──────────────────────────────────────────────────────
    submitDropOffSurvey: builder.mutation({
      query: (body) => ({
        url: "s/submitDropOffSurvey",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getDropOffSurveyStatus: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(
          `s/getDropOffSurveyStatus?brand_id=${brandId}`
        ),
      providesTags: ["DropOffSurveyStatus"],
    }),

    // ─── Intent-capture form (SEL-823) ────────────────────────────────────────
    submitIntentCapture: builder.mutation({
      query: (body) => ({
        url: "s/submitIntentCapture",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getIntentCaptureStatus: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(
          `s/getIntentCaptureStatus?brand_id=${brandId}`
        ),
      providesTags: ["IntentCaptureStatus"],
    }),
    getAttributeFilters: builder.query({
      query: ({
        collectionId,
        brandId,
        subcategoryId,
        categoryId = 0,
        filters,
        plpCollectionId = 0,
      }) => {
        const filter = filters
          ? encodeURIComponent(JSON.stringify(filters))
          : encodeURIComponent("[]");
        let url = `s/getAttributeFilters?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}&filters=${filter}`;
        if (plpCollectionId) url += `&plpCollectionId=${plpCollectionId}`;
        return getQueryStringWithCreator(url);
      },
      providesTags: ["attributeFilters"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetCollectionDetailsQuery,
  useGetPLPCollectionsQuery,
  useLazyGetCollectionProductsQuery,
  useLazyGetFilterSizeOptionsQuery,
  useLazyGetFilterColorOptionsQuery,
  useLazyGetFilterBrandOptionsQuery,
  useLazyGetFilterCategoriesOptionsQuery,
  useLazyGetFilterDiscountOptionsQuery,
  useLazyGetFilterTagsOptionsQuery,
  useLazyGetFilterGenderOptionsQuery,
  useLazyGetAttributeFiltersQuery,
  useGetCollectionProductsQuery,
  useSubmitDropOffSurveyMutation,
  useGetDropOffSurveyStatusQuery,
  useSubmitIntentCaptureMutation,
  useGetIntentCaptureStatusQuery,
} = sourcingCollectionsReduxAPI;
